import type { components } from "@/api/contract";

/** Plan ids returned for current and legacy subscribers. */
export type PlanId = components["schemas"]["MeResponse"]["plan_id"];
/** Plans intentionally available through a new Polar checkout. */
export type PaidPlanId = components["schemas"]["CheckoutRequest"]["plan"];
export type CheckoutInterval = components["schemas"]["CheckoutRequest"]["interval"];
export type BillingProvider = components["schemas"]["SubscriptionManagementCapability"]["provider"];
export type CatalogPlanId = "free" | PaidPlanId | "business";

export const APP_STORE_SUBSCRIPTIONS_URL = "https://apps.apple.com/account/subscriptions";

export interface PlanPricing {
  /** USD charged per month when billed monthly. */
  monthly: number;
  /** USD charged once per year when billed yearly. */
  yearly: number;
}

export interface Plan {
  id: CatalogPlanId;
  i18nKey: CatalogPlanId;
  pricing: PlanPricing;
  /** The visually emphasised hero plan. */
  highlighted: boolean;
}

/**
 * Prices mirror the Polar operator configuration. Business is intentionally a
 * non-checkout design-partner target until shared balances and seats exist.
 */
export const PLANS: Plan[] = [
  { id: "free", i18nKey: "free", pricing: { monthly: 0, yearly: 0 }, highlighted: false },
  {
    id: "creator",
    i18nKey: "creator",
    pricing: { monthly: 19, yearly: 204 },
    highlighted: false,
  },
  { id: "power", i18nKey: "power", pricing: { monthly: 39, yearly: 420 }, highlighted: true },
  { id: "studio", i18nKey: "studio", pricing: { monthly: 89, yearly: 960 }, highlighted: false },
  {
    id: "business",
    i18nKey: "business",
    pricing: { monthly: 249, yearly: 2700 },
    highlighted: false,
  },
];

const PLAN_RANK: Record<PlanId, number> = {
  free: 0,
  creator: 1,
  power: 2,
  studio: 3,
  // Legacy plans remain readable for existing web / App Store subscribers.
  pro: 1,
  max: 3,
};

export function isPaidPlan(id: CatalogPlanId): id is PaidPlanId {
  return id === "creator" || id === "power" || id === "studio";
}

export function isSubscriptionPlan(id: PlanId): boolean {
  return id !== "free";
}

export function comparePlans(a: PlanId, b: PlanId): number {
  return PLAN_RANK[a] - PLAN_RANK[b];
}

export function toPlanId(value: string | null | undefined): PlanId {
  switch (value) {
    case "creator":
    case "power":
    case "studio":
    case "pro":
    case "max":
      return value;
    default:
      return "free";
  }
}

/**
 * Existing paid subscribers change products through Polar's portal to avoid
 * accidentally creating a second subscription. Business is sales-assisted.
 */
export type PlanCtaKind =
  | "current"
  | "checkout"
  | "unavailable"
  | "manage"
  | "app_store"
  | "contact"
  | "support";

export function planCta(
  plan: CatalogPlanId,
  current: PlanId,
  provider: BillingProvider,
  checkoutEnabled = true,
): PlanCtaKind {
  if (plan === "business") return "contact";
  if (plan === current) return "current";
  if (current !== "free") {
    if (provider === "polar") return "manage";
    if (provider === "app_store") return "app_store";
    return "support";
  }
  if (plan === "free") return "current";
  return checkoutEnabled ? "checkout" : "unavailable";
}

export interface DisplayPrice {
  /** Headline amount (USD) for the chosen interval. */
  amount: number;
  /** Effective per-month price for a yearly plan; null when billed monthly. */
  perMonth: number | null;
}

export function displayPrice(pricing: PlanPricing, interval: CheckoutInterval): DisplayPrice {
  if (interval === "year") {
    const perMonth = pricing.yearly === 0 ? 0 : Math.round((pricing.yearly / 12) * 100) / 100;
    return { amount: pricing.yearly, perMonth };
  }
  return { amount: pricing.monthly, perMonth: null };
}

export function formatUsd(amount: number): string {
  return Number.isInteger(amount) ? `$${amount}` : `$${amount.toFixed(2)}`;
}
